import React, { forwardRef } from "react";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import "./Appointments.scss";
import AddAppointment from "./components/AddAppointment";
import LoadingFullScreen from "../../components/LoadingFullScreen";
import SelectWaitingRoom from "./components/SelectWaitingRoom";
import UploadAppointments from "./components/UploadAppointments";
import {
  Workflow,
  AppointmentWorkflowStatus,
  ValidStateTransition,
  ValidStateTransitionAction,
  AppointmentState,
  Appointment
} from "./types";
import { Page, RootState } from "../types";
import { useSelector, useDispatch } from "react-redux";
import AppointmentLineEntry from "./components/AppointmentLineEntry";
import { Organization } from "../organizations/types";
import { get, post, put } from "../../utils/fetch";
import flash from "../flash/actions";
import useInterval from "../../utils/useInterval";
import { User } from "../session/types";
import history from "../../history";
import { PubNubWorkflowState } from "../pubnub/workflow/reducer";
import Appointments from "../appointmentsDemo/components/Appointments";

export interface DatePickerInterface {
  value: Date;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const AppointmentPage: React.FunctionComponent = () => {
  const ref = React.createRef();
  const [loading, setLoading] = useState<boolean>(false);
  const [passiveLoading, setPassiveLoading] = useState<boolean>(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const pubNubWorkflow: PubNubWorkflowState = useSelector(
    (state: RootState) => state.pubnub.workflow
  );
  const [appointments, setAppointments] = useState<Page<AppointmentWorkflowStatus> | null>(null);
  const defaultPageSize = 25;
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const setAppointmentDateWithTime = (date: Date) => {
    return moment(date)
      .set({ hour: 8, minute: 0, seconds: 0 })
      .toDate();
  };

  const [appointmentDate, setAppointmentDate] = useState<Date>(
    setAppointmentDateWithTime(new Date())
  );
  const [isAppointmentsStale, setIsAppointmentsStale] = useState<boolean>(false);
  const currentOrganization: Organization | null = useSelector(
    (state: RootState) => state.orgs.currentOrganization
  );
  const currentUser: User | null = useSelector(
    (state: RootState) => state.session.data.currentUser
  );

  const dispatch = useDispatch();

  const getAppointments = async (isUpdating?: boolean) => {
    if (!currentOrganization || !selectedWorkflow) return;

    if (!isUpdating) setLoading(true);

    setIsAppointmentsStale(false);

    const after = moment(appointmentDate)
      .startOf("day")
      .toISOString();
    const before = moment(appointmentDate)
      .endOf("day")
      .toISOString();
    const url = `/api/organization/${currentOrganization.id}/workflows/${
      selectedWorkflow.id
    }/appointments/?after=${after}&before=${before}&page=1&page_size=${
      isUpdating ? pageSize : defaultPageSize
    }`;
    const resp = await get<Page<AppointmentWorkflowStatus>>(url);

    if (resp.data != appointments && !isAppointmentsStale) {
      setAppointments(resp.data);
    }

    setLoading(false);
  };

  const updateAppointmentStatus = pubNubWorkflow => {
    if (!appointments || !selectedWorkflow) {
      return;
    }
    const updatedAppointment = appointments.results.find(
      x => x.id === pubNubWorkflow[selectedWorkflow.id].workflow_status_id
    );
    getUpdatedAppointment(updatedAppointment);
  };

  const getUpdatedAppointment = async updatedAppointment => {
    if (!currentOrganization || !appointments || !updatedAppointment || !selectedWorkflow) {
      return;
    }
    const url = `/api/organization/${currentOrganization.id}/workflows/${selectedWorkflow.id}/appointments/${updatedAppointment.id}/`;
    // Need an api change for this to work
    const resp = await get<AppointmentWorkflowStatus>(url);

    if (resp.data) {
      // Need to update this individual appointment in the list
      const newAppointments = appointments.results
        .map(a => (a.id === updatedAppointment.id ? resp.data : a))
        .sort((a, b) => {
          // sort by appt start
          if (moment(a.appointment.start) < moment(b.appointment.start)) return -1;
          if (moment(b.appointment.start) < moment(a.appointment.start)) return 1;
          // then by status
          if (a?.state.localeCompare(b?.state) < 0) return -1;
          if (a?.state.localeCompare(b?.state) > 0) return 1;
          // finally by modified
          if (moment(a.modified) < moment(b.modified)) return -1;
          if (moment(b.modified) < moment(a.modified)) return 1;
        });
      setAppointments({ ...appointments, results: newAppointments });
    }

    setLoading(false);
  };

  // For new workflow events
  useEffect(() => {
    if (!pubNubWorkflow || Object.keys(pubNubWorkflow).length === 0) return;
    updateAppointmentStatus(pubNubWorkflow);
  }, [pubNubWorkflow]);

  useEffect(() => {
    setPageSize(defaultPageSize);

    getAppointments();

    if (currentUser?.featureFlags?.enableVirtualWaitingRoom === false) {
      history.push("/dashboard/");
    }
  }, [appointmentDate, selectedWorkflow]);

  // this type for onCLick is not correct. Need to figure that out.
  const DatePickerTemplate = forwardRef(({ value, onClick }: DatePickerInterface, _ref) => (
    <button className="link date-selection" onClick={onClick}>
      <i className="far fa-calendar-alt"></i>
      {moment(value).format("dddd - LL")}
    </button>
  ));

  const updateAppointment = async (
    appointmentWorkflowStatus: AppointmentWorkflowStatus,
    selection: ValidStateTransition,
    disableFlash?: boolean
  ) => {
    setPassiveLoading(true);

    const url = `/api/organization/${currentOrganization?.id}/workflows/${selectedWorkflow?.id}/appointments/${appointmentWorkflowStatus.id}/performStateTransition/`;
    const resp = await put<AppointmentWorkflowStatus>(url, {
      transitionUid: selection.uid
    });

    if (resp.data && appointments) {
      const updatedResults = appointments.results.map(item => {
        if (item.id === resp.data?.id) {
          return resp.data;
        }
        return item;
      });
      setAppointments({ ...appointments, results: updatedResults });

      if (!disableFlash) dispatch(flash("Appointment has been updated.", "info"));
    }
    setIsAppointmentsStale(true);
    setPassiveLoading(false);
  };

  const addAppointmentToList = (appointmentWorkflowStatus: AppointmentWorkflowStatus) => {
    if (
      moment(appointmentWorkflowStatus.appointment.start)
        .startOf("day")
        .isSame(moment(appointmentDate).startOf("day")) &&
      appointments
    ) {
      setAppointments({
        ...appointments,
        results: appointments?.results.concat(appointmentWorkflowStatus).sort((a, b) => {
          // sort by appt start
          if (moment(a.appointment.start) < moment(b.appointment.start)) return -1;
          if (moment(b.appointment.start) < moment(a.appointment.start)) return 1;
          // then by status
          if (a?.state.localeCompare(b?.state) < 0) return -1;
          if (a?.state.localeCompare(b?.state) > 0) return 1;
          // finally by modified
          if (moment(a.modified) < moment(b.modified)) return -1;
          if (moment(b.modified) < moment(a.modified)) return 1;
        })
      });
    }
    dispatch(flash("Appointment has been created.", "info"));
  };

  const getMoreAppointments = async () => {
    setPassiveLoading(true);
    if (!appointments) return;

    const url = appointments.next;
    const resp = await get<Page<AppointmentWorkflowStatus>>(url);

    if (resp && resp.data) {
      setPageSize(appointments.results.concat(resp.data.results).length);

      setAppointments({
        count: resp.data.count,
        next: resp.data.next,
        previous: resp.data.previous,
        results: appointments.results.concat(resp.data.results)
      });
    }

    setPassiveLoading(false);
  };

  const canSendConfirmationRequests = (): boolean => {
    if (
      appointments?.results.find(a => {
        return AppointmentState[a.state] === AppointmentState.scheduled;
      })
    ) {
      return true;
    }
    return false;
  };

  const sendAllConfirmationRequests = () => {
    let count = 0;
    //TODO:  fix this action. Hidden for now
    appointments?.results.forEach(apt => {
      const validStateTransition = apt.validStateTransitions.find(
        t => t.action === ValidStateTransitionAction.request_confirmation
      );
      if (validStateTransition) {
        count += 1;
        updateAppointment(apt, validStateTransition, true);
      }
    });
    if (count > 0) {
      dispatch(flash(`Confirmation requests have been sent.`, "info"));
    }
  };

  const createDemoSlate = async () => {
    if (!currentOrganization || !selectedWorkflow) return;

    setLoading(true);

    const body = {
      demoDate: moment(appointmentDate).toISOString()
    };

    const url = `/api/organization/${currentOrganization.id}/workflows/${selectedWorkflow.id}/createDemoSlate/`;
    const resp = await post<Page<AppointmentWorkflowStatus>>(url, body);

    setIsAppointmentsStale(true);

    if (resp.data) {
      getAppointments(false);
    }

    setLoading(false);
  };

  return (
    <>
      {loading && <LoadingFullScreen />}
      {passiveLoading && <LoadingFullScreen css="passive" />}
      <div className="appointments-container width-restrict">
        <div className="flex-between-center">
          <SelectWaitingRoom
            setLoading={setLoading}
            selectedWorkflow={selectedWorkflow}
            setSelectedWorkflow={setSelectedWorkflow}
          />
          <div className="flex">
            <AddAppointment
              setLoading={setLoading}
              selectedWorkflow={selectedWorkflow}
              addAppointmentToList={addAppointmentToList}
              currentAppointmentDate={appointmentDate}
            />
            <div className="ml-10">
              <UploadAppointments setLoading={setLoading} selectedWorkflow={selectedWorkflow} />
            </div>
          </div>
        </div>
        {selectedWorkflow && (
          <>
            <div className="sections">
              <div className="section p-15 m-15">
                <>
                  <div className="flex-between">
                    <DatePicker
                      selected={appointmentDate}
                      onChange={(date: Date) =>
                        setAppointmentDate(setAppointmentDateWithTime(date))
                      }
                      customInput={
                        <DatePickerTemplate ref={ref} value={appointmentDate} onClick={onclick} />
                      }
                    />
                  </div>

                  <div className="flex list-header mb-10 mt-20">
                    <div className="flex-2">Patient</div>
                    <div className="flex-1">Time</div>
                    <div className="flex-2">Status</div>
                    <div className="action-column"></div>
                  </div>

                  {appointments &&
                    appointments.results.map(apt => (
                      <AppointmentLineEntry
                        key={apt.id}
                        appointmentWorkflowStatus={apt}
                        updateAppointment={updateAppointment}
                        selectedWorkflow={selectedWorkflow}
                      />
                    ))}

                  {appointments?.results.length == 0 && (
                    <div className="flex-justify-center mt-50 mb-30 bold">
                      There are no scheduled appointments on this day.
                    </div>
                  )}

                  {appointments?.next && (
                    <button className="m-15" onClick={getMoreAppointments}>
                      Show More Appointments
                    </button>
                  )}
                </>
              </div>
              {appointments && (
                <div className="section p-15 mb-25 mt-25">
                  <h1>Actions</h1>
                  <div className="flex-between">
                    {currentUser?.featureFlags?.enableVwrCreateDemo && (
                      <button onClick={() => createDemoSlate()}>Create Appointments</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AppointmentPage;
